import { createContext, useContext, useEffect } from "react";
import * as React from "react";
import { isProduction } from "../utils/envUtils";
import { useLocation } from "react-router";

export class GoogleAnalyticsEvents {
    get user() {
        return {
            created: {
                account: (type: "with-payment-info" | "without-payment-info") =>
                    makeEvent(
                        "user",
                        "created",
                        type === "with-payment-info" ? "account-with-payment-info" : "account-without-payment-info"
                    ),
                billingInfo: () => makeEvent("user", "created", "billing-info"),
            },
        };
    }
}

function makeEvent(category: string, action: string, label?: string, value?: number): GoogleAnalyticsEvent {
    return {
        category,
        action,
        label,
        value,
    };
}

const productionGtm = "GTM-TDH4JWF2";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const dataLayer = ((window as any).dataLayer = (window as any).dataLayer || []);

const gtag = function (...args) {
    dataLayer.push(...args);
};

const events = new GoogleAnalyticsEvents();

export const reportEvent = (eventProvider: (builder: GoogleAnalyticsEvents) => GoogleAnalyticsEvent) => {
    const event = eventProvider(events);
    if (isProduction()) {
        gtag({
            event: "gtm-event",
            eventAction: event.action,
            eventCategory: event.category,
            eventLabel: event.label,
            eventValue: event.value,
        });
    } else {
        // tslint:disable-next-line:no-console
        console.info("Skipping reporting event to GA (not production env):", event);
    }
};

export interface GoogleAnalyticsEvent {
    category: string;
    action: string;
    label?: string;
    value?: number;
}

export type GoogleTagManagerReporter = (
    eventProvider: (builder: GoogleAnalyticsEvents) => GoogleAnalyticsEvent
) => void;

export interface GoogleTagManagerHook {
    reportEvent: GoogleTagManagerReporter;
}

const googleTagManagerContext = createContext<GoogleTagManagerHook>(undefined);

export function ProvideGoogleTagManager({
    children,
    override,
}: {
    override?: GoogleTagManagerHook;
    children: React.ReactNode;
}) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const gtm = override || useProvideGoogleTagManager();
    return (
        <googleTagManagerContext.Provider value={gtm}>
            <GTMNoScript />
            <div>{children}</div>
        </googleTagManagerContext.Provider>
    );
}

export function GTMNoScript() {
    if (isProduction()) {
        return (
            <noscript>
                <iframe
                    src={"https://www.googletagmanager.com/ns.html?id=" + productionGtm}
                    height="0"
                    width="0"
                    style={{ display: "none", visibility: "hidden" }}
                />
            </noscript>
        );
    } else {
        return null;
    }
}

function useProvideGoogleTagManager(): GoogleTagManagerHook {
    const location = useLocation();

    useEffect(() => {
        if (!isProduction()) {
            return;
        }

        (function (w, d, s, l, i) {
            w[l] = w[l] || [];
            w[l].push({
                "gtm.start": new Date().getTime(),
                event: "gtm.js",
            });
            const f = d.getElementsByTagName(s)[0];
            const j = d.createElement(s) as HTMLScriptElement;
            const dl = l != "dataLayer" ? "&l=" + l : "";
            j.async = true;
            j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
            f.parentNode.insertBefore(j, f);
        })(window, document, "script", "dataLayer", productionGtm);
    }, []);

    useEffect(() => {
        dataLayer.push({ event: "virtualPageView" });
    }, [location]);

    return {
        reportEvent,
    };
}

export function useGoogleTagManager() {
    return useContext(googleTagManagerContext);
}
